import { Spinner } from '@finn/design-system/atoms/spinner';
import { showToast } from '@finn/design-system/atoms/toast';
import { CheckCircleFilled } from '@finn/design-system/icons/check-circle-filled';
import { DeleteFilled } from '@finn/design-system/icons/delete-filled';
import { PhotoCameraFilled } from '@finn/design-system/icons/photo-camera-filled';
import { Image } from '@finn/ui-components';
import { cn } from '@finn/ui-utils';
import axios from 'axios';
import {
  ChangeEvent,
  ForwardedRef,
  forwardRef,
  useEffect,
  useState,
} from 'react';
import {
  ControllerProps,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';

import { useDealId } from '../../../providers/MetadataProvider';
import { Field } from '../types';
import { FormItem } from './form';

type ImagesUploadFieldProps = Field &
  Omit<Parameters<ControllerProps['render']>['0']['field'], 'ref'>;

const UPLOAD_ENDPOINT = '/portal/api/bff/active-fleet/damage-reports/images';

// TODO should be passed from outside, as this is a bussness logic
// hardcocded in shareable component
const uploadFile = async ({
  file,
  onUploadProgress,
  subscription_id,
  timestamp,
}: {
  file: File;
  onUploadProgress: (progress: number) => void;
  subscription_id: string;
  timestamp: string;
}) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.set('subscription_id', subscription_id);
  // TODO should be named smth like session id or user_id
  // used to represent any images uploaded during the session
  formData.set('timestamp', timestamp);

  const { data } = await axios.post(UPLOAD_ENDPOINT, formData, {
    onUploadProgress: (progressEvent) => {
      const total = progressEvent.total;
      const percentCompleted = total
        ? Math.round((progressEvent.loaded * 100) / total)
        : 0;
      onUploadProgress(percentCompleted);
    },
  });

  return data;
};

export const ImagesUploadField = forwardRef(
  (
    { ...field }: ImagesUploadFieldProps,
    ref: ForwardedRef<HTMLInputElement>
  ) => {
    const dealId = useDealId();
    const { control, setValue, setError, clearErrors } = useFormContext();
    const timestamp = useWatch({ name: 'timestamp' });

    // TODO should be named smth like session id or user_id
    // used to represent any images uploaded during the session
    // we set it in such tricky way to ensure one id for whole form run, even
    // if user will jump between steps
    useEffect(() => {
      if (!timestamp) {
        setValue('timestamp', String(Date.now()));
      }
    }, [timestamp, setValue]);

    const { fields, append, remove, insert } = useFieldArray({
      control,
      name: 'images',
    });
    const [uploadingState, setUploadingState] = useState<
      Record<string, number>
    >(
      field?.value?.length
        ? field?.value?.reduce(
            (acc: Record<string, number>, value: any) => {
              acc[value?.id] = value?.path ? 100 : 0; // If path exists, we assume upload is complete

              return acc;
            },
            {} as Record<string, number>
          )
        : {}
    );

    if (field.type !== 'images-upload') {
      return null;
    }

    const handleFilesSelected = async (e: ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      const MAX_SIZE = 6; // MB

      setError('uploading', {
        type: 'manual',
        message: 'Images are still uploading…',
      });

      if (files.some((file) => file.size > MAX_SIZE * 1024 * 1024)) {
        // If any file is larger than MAX_SIZE, show an error or handle it accordingly
        showToast({
          status: 'error',
          title: 'Einige Dateien sind zu groß.',
          description: `Die maximale Dateigröße beträgt ${MAX_SIZE} MB.`,
        });

        return;
      }

      const res = files
        .filter((file) => {
          return file.size <= MAX_SIZE * 1024 * 1024; // Filter out files larger than MAX_SIZE
        })
        .map(async (file, index) => {
          const id = `${file.name}-${Math.random().toString(36).substring(7)}-${index}`;
          append({ file, path: '', id });

          // we set initial upload progress to 10% for the new file
          // just UX hack
          setUploadingState((prevState) => {
            return {
              ...prevState,
              [id]: 10, // Initialize the upload progress for the new file
            };
          });

          // we start fake UI only progress update, +10% at a time each 200ms
          // up to 90%, as for last step we will wait for API response
          const intervalId = setInterval(() => {
            setUploadingState((prevState) => {
              const currentProgress = prevState[id] || 0;
              if (currentProgress < 80) {
                return {
                  ...prevState,
                  [id]: currentProgress + 10, // Simulate upload progress
                };
              }

              clearInterval(intervalId);

              return prevState; // Stop updating when progress reaches 100%
            });
          }, 200); // Update progress every 200ms

          // now we start actual file upload
          // when progress reaches 100% we will update state to show success icon
          // we will do small delay to ensure that the progress bar is visible

          return {
            promise: uploadFile({
              file,
              timestamp,
              onUploadProgress: (progress) => {
                if (progress === 100) {
                  clearInterval(intervalId); // Stop the interval when upload is complete
                  setTimeout(() => {
                    setUploadingState((prevState) => ({
                      ...prevState,
                      [id]: 100, // Set final progress to 100%
                    }));
                  }, 300);
                }
              },
              subscription_id: dealId || '',
            }),
            id,
            file,
          };
        });

      try {
        const responses = await Promise.all(res);
        await Promise.all(
          responses.map(async ({ promise, id, file }, index) => {
            const response = await promise;

            const indexToReplace = fields?.length + index;
            remove(indexToReplace);
            insert(indexToReplace, {
              file,
              id,
              path: response.path?.location,
            });
          })
        );
      } catch (_err) {
        // ignore
      } finally {
        clearErrors('uploading');
      }

      e.target.value = '';
    };

    const handleDeleteImage = (id: string) => () => {
      remove(fields.findIndex((item) => item.id === id));
    };

    // TODO ideally we want to add new variant for DS input that supports multiple files
    // and then we can use it here instead of the label and input directly
    return (
      <FormItem className={field.className}>
        {field?.value
          ?.slice()
          ?.sort(
            (a: any, b: any) => a?.file?.lastModified - b?.file?.lastModified
          )
          .map((item: { id: string; file: File }, index: number) => (
            <div
              key={`${item.id}-${index}`}
              className="relative flex w-full items-center gap-x-2 gap-y-4"
            >
              <div className="relative">
                <Image
                  className="h-14 w-[72px] rounded-sm"
                  alt={item.file?.name || 'Uploaded image'}
                  src={item.file ? URL.createObjectURL(item.file) : ''}
                />
                <DeleteFilled
                  onClick={handleDeleteImage(item.id)}
                  role="button"
                  tabIndex={0}
                  className="absolute left-1/2 top-1/2 z-base size-4 -translate-x-1/2 -translate-y-1/2 fill-white"
                />
              </div>
              <div className="flex w-full flex-col gap-4">
                <p className="relative body-14-regular">
                  {item.file?.name}
                  <div className="absolute right-0 top-1/2 size-4 -translate-y-1/2">
                    {uploadingState[item?.id] === 100 ? (
                      <CheckCircleFilled className="size-4 fill-green" />
                    ) : (
                      <Spinner className="size-4 fill-trustedBlue" />
                    )}
                  </div>
                </p>
                {uploadingState[item?.id] === 100 ? null : (
                  <div className="h-1 overflow-hidden rounded-sm bg-pewter">
                    <div
                      style={{ width: `${uploadingState[item?.id]}%` }}
                      className="h-full bg-trustedBlue transition-all"
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        <label
          className={cn(
            'flex cursor-pointer items-center gap-2',
            field?.value?.length ? '!mt-4' : '!mt-0'
          )}
        >
          <PhotoCameraFilled className="size-3.5" />
          <p className="body-14-semibold">
            {field.label}
            <hr className="w-full border-b-0 border-black" />
          </p>
          <input
            ref={ref}
            type="file"
            accept="image/*"
            multiple
            disabled={field?.disabled}
            className="hidden"
            id="multi-image-uploader-input"
            onChange={handleFilesSelected}
          />
        </label>
      </FormItem>
    );
  }
);
